import React, { ChangeEvent } from "react";
import { Button } from "@mui/material";
import { getIn } from "formik";
import { useTranslation } from "react-i18next";
import { FormikErrors } from "formik/dist/types";

import {
  ErrorLabel,
  FormikInputField,
  FormikSelectField,
  FormRow,
} from "components";
import { DocumentTypeEnum } from "enums";
import { DocumentItem } from "models";
import { HouseholdMemberShort } from "graphql/query/caseByIdShort";

import { documentTypeOptions } from "./formUtils";
import { UploadFormValues } from "./UploadDocuments";
import {
  ButtonContainer,
  GridContainer,
  GridRowEnd,
  GridRowItem,
  RemoveButton,
} from "./UploadDocuments.css";

interface Props {
  index: number;
  errors: FormikErrors<UploadFormValues>;
  member: HouseholdMemberShort;
  isMainApplicant: boolean;
  setFieldValue: (
    field: string,
    value: File | undefined,
    shouldValidate?: boolean,
  ) => void;
  addDocument: (doc: DocumentItem) => void;
  removeDocument: (docIndex: number) => void;
}

export const UploadDocumentRow = ({
  index,
  errors,
  member,
  isMainApplicant,
  addDocument,
  removeDocument,
  setFieldValue,
}: Props) => {
  const { t } = useTranslation();

  const onAddDocument = () => {
    addDocument({
      documentDescription: t(
        isMainApplicant
          ? "upload_documents_description_main_applicant"
          : "upload_documents_description_dependent",
      ),
      documentUploadType: isMainApplicant
        ? DocumentTypeEnum.GOVERNMENT_ID
        : DocumentTypeEnum.IMMIGRATION_DOCUMENT,
      file: undefined,
    });
  };

  const onRemoveDocument = (documentIndex: number) => {
    removeDocument(documentIndex);
  };


  return (
    <React.Fragment key={index}>
      <FormRow>
        <input type="hidden" name="Content-Disposition" value="inline" />
        <GridContainer>
          <GridRowItem>
            <FormikInputField
              disabled
              fullWidth
              label="client_name"
              name={`uploadDocumentsMembers.${index}.name`}
            />
          </GridRowItem>
          {member.documentsList.map(
            (_: DocumentItem, documentIndex: number) => {
              const inputError = getIn(
                errors,
                `uploadDocumentsMembers[${index}].documentsList[${documentIndex}].file`,
              );
              return (
                <React.Fragment key={documentIndex}>
                  <GridRowItem
                    sx={{ display: "none" }}
                    aria-hidden
                  >
                    <FormikSelectField
                      disabled
                      options={documentTypeOptions}
                      label="document_type"
                      setUntranslatedValue={true}
                      name={`uploadDocumentsMembers.${index}.documentsList.${documentIndex}.documentUploadType`}
                    />
                  </GridRowItem>
                  <GridRowItem>
                    <FormikInputField
                      disabled
                      multiline
                      minRows={2}
                      label="document_description"
                      name={`uploadDocumentsMembers.${index}.documentsList.${documentIndex}.documentDescription`}
                    />
                  </GridRowItem>
                  <GridRowItem>
                    <>
                      <input
                        name={`uploadDocumentsMembers.${index}.documentsList.${documentIndex}.file`}
                        type="file"
                        accept={"image/*, .pdf"}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setFieldValue(
                              `uploadDocumentsMembers.${index}.documentsList.${documentIndex}.file`,
                              file,
                              true,
                            );
                          }
                        }}
                      />
                      {inputError && (
                        <div>
                          <ErrorLabel message={inputError} />
                        </div>
                      )}
                    </>
                  </GridRowItem>
                  <GridRowEnd>
                    <RemoveButton
                      variant="contained"
                      type="button"
                      onClick={() => onRemoveDocument(documentIndex)}
                    >
                      Remove
                    </RemoveButton>
                  </GridRowEnd>
                </React.Fragment>
              );
            },
          )}
        </GridContainer>
      </FormRow>
      <ButtonContainer>
        <Button
          fullWidth
          variant={"contained"}
          type="button"
          onClick={onAddDocument}
        >
          {t('upload_another_document')}
        </Button>
      </ButtonContainer>
    </React.Fragment>
  );
};
